import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useMount, useTitle, useUpdateEffect } from "react-use";
import { useRecoilState, useRecoilValue } from "recoil";
import { addUser, getUserLectureList, getUsersByLecture } from "../api";
import { Button } from "../components/common/Forms";
import Icon from "../components/common/Icon";
import { ori_headers, userList } from "../states/atoms/userState";
import { autoHyphen } from "../util/common";
import { signItem } from "../states/atoms/signState";
import UploadModal from "../components/user/UploadModal";

const ManageUser = () => {
  useTitle("THE MAKER 회원 정보");
  const [{ headers, contents, widths }, setUserList] = useRecoilState(userList);
  const signInfo = useRecoilValue(signItem);
  const [lectureList, setLectures] = useState([]);
  const [selectedLecture, setSelected] = useState(null);
  const [{ sort, asc }, setSort] = useState({ sort: 0, asc: true });
  const [type, setType] = useState("imweb"); // imweb, csv, direct
  const [modal, setModal] = useState({
    isOpen: false,
    loading: false,
    result: null,
    meesage: "",
    error: false,
  });
  const [userFile, setFile] = useState(null);
  const [auth, setAuth] = useState(false);
  const queryClient = useQueryClient();

  const userMttn = useMutation({
    mutationFn: addUser,
    onSettled: (data) => {
      setModal({ ...modal, result: null, message: "", error: false });
      if (data.result) {
        setModal({
          ...modal,
          error: false,
          loading: false,
          message: "회원 정보 업로드가 완료되었습니다.",
          result: true,
        });
      } else {
        setModal({
          ...modal,
          loading: false,
          message: "회원 정보 업로드 중 오류가 발생했습니다.",
          error: true,
          result: false,
        });
        setFile(null);
      }

      queryClient.invalidateQueries({ queryKey: ["user/upload"] });
    },
  });

  useMount(async () => {
    const result = await queryClient.fetchQuery(["user/lecture/list"], {
      queryFn: getUserLectureList,
    });
    queryClient.invalidateQueries({ queryKey: ["user/lecture/list"] });
    if (result.result) {
      const arr = [...result.data];
      // arr.unshift({ _id: "", title: "신청 전" });
      arr.unshift({ _id: null, title: "강의를 선택하세요." });
      setLectures(arr);
    }
  });

  const getQueryUsers = async () => {
    if (selectedLecture) {
      const id = selectedLecture;
      const result = await queryClient.fetchQuery([`users/${id}`], {
        queryFn: () =>
          getUsersByLecture({
            id,
            sort: ori_headers[sort],
            asc,
          }),
      });
      console.log(result);
      if (result.result) {
        const contents = result.data.map((item, i) => {
          const {
            // id,
            // parent_key,
            // userId,
            parent_id,
            student_id,
            student_name,
            student_age,
            student_gender,
            student_school,
            student_tel,
            parent_tel,
            email,
            // lecture_title,
            memo,
          } = item;
          return [
            {
              key: `cell_${id}_${i}_key`,
              className: `border border-zinc-300 p-1 min-w-[7rem]`,
              value: parent_id
                ? parent_id.slice(parent_id.length - 10, parent_id.length)
                : "-",
              tooltip: parent_id || "-",
            },
            {
              key: `cell_${id}_${i}_userid`,
              className: `border border-zinc-300 p-1 min-w-[7rem]`,
              value: student_id.slice(
                student_id.length - 10,
                student_id.length
              ),
              tooltip: student_id,
            },
            {
              key: `cell_${id}_${i}_name`,
              className: `border border-zinc-300 p-1 min-w-[4rem] `,
              value: student_name || "-",
            },
            {
              key: `cell_${id}_${i}_age`,
              className: `border border-zinc-300 p-1 min-w-[3rem]`,
              value: student_age || "-",
            },
            {
              key: `cell_${id}_${i}_gender`,
              className: `border border-zinc-300 p-1 min-w-[3rem]`,
              value: student_gender || "-",
            },
            {
              key: `cell_${id}_${i}_school`,
              className: `border border-zinc-300 p-1 min-w-[8rem]`,
              value: student_school || "-",
            },
            {
              key: `cell_${id}_${i}_s_tel`,
              className: `border border-zinc-300 p-1 min-w-[8rem]`,
              value: student_tel ? autoHyphen(student_tel) : "-",
            },
            {
              key: `cell_${id}_${i}_p_tel`,
              className: `border border-zinc-300 p-1 min-w-[8rem]`,
              value: parent_tel ? autoHyphen(parent_tel) : "-",
            },
            {
              key: `cell_${id}_${i}_email`,
              className: `border border-zinc-300 p-1 min-w-[11rem]`,
              value: email || "-",
            },
            {
              key: `cell_${id}_${i}_memo`,
              className: `border border-zinc-300 p-1 min-w-[16rem]`,
              value: memo || "",
            },
          ];
        });
        setUserList({ headers, contents, widths });
      }
      queryClient.invalidateQueries([`users/${selectedLecture}`]);
    }
  };

  useUpdateEffect(() => {
    getQueryUsers();
  }, [sort, asc, selectedLecture]);

  const handleSort = (i) => () => {
    if (sort === i) {
      setSort({ sort, asc: !asc });
    } else {
      setSort({ sort: i, asc: true });
    }
  };

  const handleSelect = (e, v) => {
    if (e.target.value === "강의를 선택하세요.") {
      setSelected(null);
    } else {
      setSelected(e.target.value);
    }
  };

  const handleClose = () => {
    setFile(null);
    setModal({
      isOpen: false,
      loading: false,
      result: null,
      meesage: "",
      error: false,
    });
    if (selectedLecture) {
      getQueryUsers();
    }
  };

  const handleFileChange = (e) => {
    setModal({ ...modal, result: null, message: "", error: false });
    setFile(e.target.files);
  };

  const handleUserUpload = (blob) => {
    setModal({ ...modal, result: null, message: "", error: false });
    userMttn.mutate({
      files: userFile || [blob],
      lecture_id: selectedLecture,
      type,
    });
  };

  const handleType = (e) => {
    setType(e.target.name);
  };

  useEffect(() => {
    if (signInfo && "info" in signInfo) {
      if (signInfo.info && "role" in signInfo.info) {
        const { role } = signInfo.info;
        return setAuth(role === "admin");
      }
    }
    setAuth(false);
  }, [signInfo]);

  return (
    <div className="w-full mx-auto md:w-11/12 md:px-2">
      <h1 className="flex items-center px-2 py-4 font-sans text-xl font-bold break-normal text-cyan-500 md:text-2xl">
        회원 정보
      </h1>
      <div
        id="recipients"
        className="relative p-4 overflow-scroll bg-white rounded shadow md:p-8 lg:mt-0 scrollbar-hide"
      >
        <div className="sticky top-0 left-0 right-0 flex flex-col justify-between w-full gap-2 mb-2 md:flex-row md:items-center">
          <div className="inline-flex items-center w-full md:w-1/2">
            <label
              htmlFor="classes"
              className="block w-20 text-sm font-medium text-gray-700"
            >
              강의 선택
            </label>
            <select
              id="classes"
              name="classes"
              autoComplete="classes-name"
              className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm"
              onChange={handleSelect}
            >
              {lectureList.map(({ _id: id, title: v }) => (
                <option
                  key={`option_${v}`}
                  value={id}
                  selected={selectedLecture === id}
                >
                  {v}
                </option>
              ))}
            </select>
          </div>
          {auth && (
            <Button
              type="primary"
              size="sm"
              className="self-end w-1/2 md:w-auto"
              onClick={() => setModal({ ...modal, isOpen: true })}
            >
              <Icon name="plus" size="sm" />
              회원 정보 업로드
            </Button>
          )}
        </div>
        <table
          id="example"
          className="w-full py-4 text-sm border border-collapse table-auto cursor-default stripe hover border-zinc-400"
        >
          {headers.length > 0 && (
            <thead>
              <tr>
                {headers.map((v, i) => (
                  <th
                    key={v}
                    className={`${widths[i]} border border-zinc-200 py-2 bg-zinc-100`}
                    onClick={handleSort(i)}
                  >
                    <div className="inline-flex items-center">
                      {v}
                      {sort === i && (
                        <Icon
                          name={asc ? "bars-arrow-down" : "bars-arrow-up"}
                          size="sm"
                          className="ml-1"
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
          )}
          {selectedLecture != null && (
            <tbody>
              {contents.length > 0 &&
                contents.map((item, i) => (
                  <tr key={`row_${selectedLecture}_${i}`}>
                    {item.map(({ key, className, value, tooltip }) => (
                      <td key={key} className={`${className} group relative`}>
                        {value || "-"}
                        {!!tooltip && (
                          <span className="absolute top-0 left-0 z-50 p-2 text-xs text-white scale-0 bg-gray-800 rounded group-hover:scale-100">
                            {tooltip}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          )}
        </table>
      </div>
      <UploadModal
        {...{
          ...modal,
          userFile,
          lectureList,
          type,
          lecture: selectedLecture,
          onClose: handleClose,
          onFile: handleFileChange,
          onUpload: handleUserUpload,
          onSelect: handleSelect,
          onType: handleType,
        }}
      />
    </div>
  );
};

export default ManageUser;
